/**
 * Debug utility for creating namespaced debug loggers
 * You can enable debugging per namespace by passing debug: true
 */

/**
 * DOM Logger - Creates a visual logging window for real device debugging
 * Useful for iOS devices where console access is difficult
 */
class DomLogger {
    constructor() {
        this.container = null;
        this.logList = null;
        this.isOpen = false;
        this.maxLogs = 100; // Limit to prevent memory issues
        this.init();
    }

    init() {
        // Create container
        this.container = document.createElement('div');
        this.container.className = 'fixed bottom-0 right-0 z-[9999] transition-transform duration-300';
        this.container.style.transform = 'translateY(calc(100% - 48px))';

        // Create header with toggle and copy all button
        const header = document.createElement('div');
        header.className = 'w-full bg-black text-white px-4 py-3 flex items-center justify-between';

        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'text-sm font-bold flex items-center gap-2';
        toggleBtn.innerHTML = `
            <span>Debug Logs</span>
            <span class="toggle-icon">▲</span>
        `;
        toggleBtn.onclick = () => this.toggle();

        const copyAllBtn = document.createElement('button');
        copyAllBtn.className = 'text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded transition-colors';
        copyAllBtn.textContent = 'Copy All';
        copyAllBtn.onclick = () => this.copyAllLogs();

        header.appendChild(toggleBtn);
        header.appendChild(copyAllBtn);

        // Create log container
        const logContainer = document.createElement('div');
        logContainer.className = 'bg-black text-white overflow-y-auto';
        logContainer.style.maxHeight = '400px';
        logContainer.style.width = '100vw';
        logContainer.style.maxWidth = '600px';

        // Create log list
        this.logList = document.createElement('div');
        this.logList.className = 'p-4 space-y-2 text-xs font-mono';

        logContainer.appendChild(this.logList);
        this.container.appendChild(header);
        this.container.appendChild(logContainer);

        // Add to DOM when ready
        if (document.body) {
            document.body.appendChild(this.container);
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                document.body.appendChild(this.container);
            });
        }
    }

    toggle() {
        this.isOpen = !this.isOpen;
        const toggleIcon = this.container.querySelector('.toggle-icon');

        if (this.isOpen) {
            this.container.style.transform = 'translateY(0)';
            toggleIcon.textContent = '▼';
        } else {
            this.container.style.transform = 'translateY(calc(100% - 48px))';
            toggleIcon.textContent = '▲';
        }
    }

    log(namespace, type, ...args) {
        const logEntry = document.createElement('div');
        logEntry.className = 'border-b border-gray-700 pb-2 group relative';

        const time = new Date().toLocaleTimeString();
        const typeColors = {
            log: 'text-blue-400',
            warn: 'text-yellow-400',
            error: 'text-red-400'
        };

        const color = typeColors[type] || 'text-white';

        // Format the message
        const message = args
            .map((arg) => {
                if (typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg, null, 2);
                    } catch (e) {
                        return String(arg);
                    }
                }
                return String(arg);
            })
            .join(' ');

        // Store the full log text for copying
        const fullLogText = `[${time}] [${type.toUpperCase()}] [${namespace}]\n${message}`;

        logEntry.innerHTML = `
            <div class="flex gap-2 text-gray-400 items-center">
                <span>${time}</span>
                <span class="${color} font-bold">[${type.toUpperCase()}]</span>
                <span class="text-purple-400">[${namespace}]</span>
                <button class="copy-log-btn ml-auto opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-gray-700 px-2 py-0.5 rounded text-[10px]">
                    Copy
                </button>
            </div>
            <div class="mt-1 whitespace-pre-wrap break-all">${message}</div>
        `;

        // Add copy button click handler
        const copyBtn = logEntry.querySelector('.copy-log-btn');
        copyBtn.onclick = () => this.copyLog(fullLogText, copyBtn);

        this.logList.appendChild(logEntry);

        // Limit number of logs
        while (this.logList.children.length > this.maxLogs) {
            this.logList.removeChild(this.logList.firstChild);
        }

        // Auto-scroll to bottom
        this.logList.scrollTop = this.logList.scrollHeight;
    }

    clear() {
        this.logList.innerHTML = '';
    }

    async copyLog(text, buttonElement) {
        try {
            await navigator.clipboard.writeText(text);
            // Visual feedback
            const originalText = buttonElement.textContent;
            buttonElement.textContent = 'Copied!';
            buttonElement.classList.add('bg-green-700');
            setTimeout(() => {
                buttonElement.textContent = originalText;
                buttonElement.classList.remove('bg-green-700');
            }, 1500);
        } catch (err) {
            console.error('Failed to copy log:', err);
            buttonElement.textContent = 'Failed';
            setTimeout(() => {
                buttonElement.textContent = 'Copy';
            }, 1500);
        }
    }

    async copyAllLogs() {
        const allLogs = Array.from(this.logList.children)
            .map((entry) => {
                // Extract text content from each log entry
                const lines = entry.textContent
                    .trim()
                    .split('\n')
                    .filter((line) => line.trim());
                return lines.join('\n');
            })
            .join('\n\n');

        try {
            await navigator.clipboard.writeText(allLogs);
            // Visual feedback on the copy all button
            const copyAllBtn = this.container.querySelector('button:last-of-type');
            if (copyAllBtn) {
                const originalText = copyAllBtn.textContent;
                copyAllBtn.textContent = 'Copied!';
                copyAllBtn.classList.add('bg-green-700');
                setTimeout(() => {
                    copyAllBtn.textContent = originalText;
                    copyAllBtn.classList.remove('bg-green-700');
                }, 1500);
            }
        } catch (err) {
            console.error('Failed to copy all logs:', err);
            alert('Failed to copy logs to clipboard');
        }
    }
}

// Singleton instance
let domLoggerInstance = null;

/**
 * Get or create the DOM logger instance
 * Only creates if not in production (theme.role !== 'main')
 * @returns {DomLogger|null}
 */
function getDomLogger() {
    // Don't show DOM logger in production (main theme)
    if (window.Shopify?.theme?.role === 'main') {
        return null;
    }

    if (!domLoggerInstance) {
        domLoggerInstance = new DomLogger();
    }
    return domLoggerInstance;
}

/**
 * Creates a debug logger for a specific namespace
 * @param {string} namespace - The namespace for the logger (e.g., 'strapfinder-drawer')
 * @param {boolean} debug - Whether to enable debugging for this namespace
 * @param {boolean} useDomLog - Whether to use DOM logging instead of console (default: false)
 * @returns {Object} Object with debug and error logging functions
 */
export function createDebugger(namespace, debug = false, useDomLog = false) {
    const domLogger = useDomLog ? getDomLogger() : null;

    return {
        log(...args) {
            if (debug) {
                if (useDomLog && domLogger) {
                    domLogger.log(namespace, 'log', ...args);
                }
                console.log(`[${namespace}]`, ...args);
            }
        },
        error(...args) {
            if (useDomLog && domLogger) {
                domLogger.log(namespace, 'error', ...args);
            }
            console.error(`[${namespace}]`, ...args);
        },
        warn(...args) {
            if (useDomLog && domLogger) {
                domLogger.log(namespace, 'warn', ...args);
            }
            console.warn(`[${namespace}]`, ...args);
        }
    };
}

/**
 * Standalone DOM logging functions for direct use
 * Useful for quick debugging without creating a debugger instance
 */
export const domLog = {
    log(...args) {
        const logger = getDomLogger();
        if (logger) {
            logger.log('global', 'log', ...args);
        }
        console.log('[domLog]', ...args);
    },
    warn(...args) {
        const logger = getDomLogger();
        if (logger) {
            logger.log('global', 'warn', ...args);
        }
        console.warn('[domLog]', ...args);
    },
    error(...args) {
        const logger = getDomLogger();
        if (logger) {
            logger.log('global', 'error', ...args);
        }
        console.error('[domLog]', ...args);
    },
    clear() {
        const logger = getDomLogger();
        if (logger) {
            logger.clear();
        }
    }
};
